import { apiClient } from '@app/core';
import { WebsiteConfig } from '../types/websiteConfig';

export const websiteConfigService = {
  /**
   * Get website configuration for a shop
   */
  async getConfig(shopId: string): Promise<WebsiteConfig> {
    console.log(`🔍 [WebsiteConfigService] Fetching config for shop: ${shopId}`);
    try {
      const response = await apiClient.get(`/api/shops/${shopId}/website-config`);
      console.log('✅ [WebsiteConfigService] Config fetched successfully');
      const config = response.data.data || response.data;
      // Mark as existing config by ensuring timestamps are present
      return config;
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log('📝 [WebsiteConfigService] No config found (404), returning default config');
        // Return default config without timestamps to indicate it's new
        const defaultConfig = this.createDefaultConfig(shopId);
        // Remove timestamps to clearly indicate this is a new config
        delete (defaultConfig as any).createdAt;
        delete (defaultConfig as any).updatedAt;
        return defaultConfig;
      }
      console.error('❌ [WebsiteConfigService] Failed to fetch config:', error);
      throw error;
    }
  },

  /**
   * Save new website configuration (POST)
   */
  async saveConfig(config: WebsiteConfig): Promise<WebsiteConfig> {
    console.log(`💾 [WebsiteConfigService] Creating new config for shop: ${config.shopId}`);
    console.log('📤 [WebsiteConfigService] Sending config:', JSON.stringify(config, null, 2));
    try {
      const response = await apiClient.post(
        `/api/shops/${config.shopId}/website-config`,
        config
      );
      console.log('✅ [WebsiteConfigService] Config created successfully');
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ [WebsiteConfigService] Failed to create config:', error);
      throw error;
    }
  },

  /**
   * Update existing website configuration (PUT)
   */
  async updateConfig(config: WebsiteConfig): Promise<WebsiteConfig> {
    console.log(`🔄 [WebsiteConfigService] Updating existing config for shop: ${config.shopId}`);
    console.log('📤 [WebsiteConfigService] Sending config:', JSON.stringify(config, null, 2));
    console.log('📤 [WebsiteConfigService] Block configs:', config.blocks.map(b => ({ id: b.id, type: b.type, config: b.config })));
    try {
      const response = await apiClient.put(
        `/api/shops/${config.shopId}/website-config`,
        config
      );
      console.log('✅ [WebsiteConfigService] Config updated successfully');
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ [WebsiteConfigService] Failed to update config:', error);
      throw error;
    }
  },

  /**
   * Publish website
   */
  async publish(shopId: string): Promise<{ liveUrl: string; success: boolean }> {
    console.log(`🚀 [WebsiteConfigService] Publishing website for shop: ${shopId}`);
    try {
      const response = await apiClient.post(
        `/api/shops/${shopId}/website-config/publish`
      );
      console.log('✅ [WebsiteConfigService] Website published successfully');
      return response.data;
    } catch (error) {
      console.error('❌ [WebsiteConfigService] Failed to publish:', error);
      throw error;
    }
  },

  /**
   * Get available templates
   */
  async getTemplates(): Promise<WebsiteConfig[]> {
    console.log('🔍 [WebsiteConfigService] Fetching templates');
    try {
      const response = await apiClient.get('/api/website-templates');
      console.log('✅ [WebsiteConfigService] Templates fetched successfully');
      return response.data;
    } catch (error) {
      console.error('❌ [WebsiteConfigService] Failed to fetch templates:', error);
      return [];
    }
  },

  /**
   * Create default configuration
   */
  createDefaultConfig(shopId: string): WebsiteConfig {
    return {
      shopId,
      version: '1.0',
      theme: {
        fontFamily: 'Poppins, sans-serif',
        primaryColor: '#34C759',
        secondaryColor: '#F5F5F5',
        accentColor: '#FF9500',
        backgroundColor: '#FFFFFF',
        textColor: '#1D1D1F',
        borderRadius: '8px',
        buttonStyle: 'rounded',
        layoutMode: 'modern',
        spacing: {
          small: '8px',
          medium: '16px',
          large: '24px',
          xlarge: '48px'
        },
        typography: {
          h1Size: '32px',
          h2Size: '24px',
          h3Size: '20px',
          bodySize: '16px',
          smallSize: '14px',
          h1Weight: '700',
          h2Weight: '600',
          bodyWeight: '400'
        }
      },
      header: {
        logoHeight: '60px',
        showTitle: true,
        showSearchBar: true,
        showCartIcon: true,
        showUserIcon: true,
        style: 'sticky'
      },
      footer: {
        copyright: `© ${new Date().getFullYear()} All rights reserved`,
        showSocialLinks: true,
        showContactInfo: true,
        showNewsletter: false
      },
      blocks: [],
      seo: {
        metaTitle: 'Welcome to Our Shop',
        metaDescription: 'Shop fresh products delivered to your doorstep',
        metaKeywords: 'shop, products, delivery, online shopping',
        robots: 'index, follow'
      }
      // Note: createdAt and updatedAt are intentionally omitted
      // They will be added by the backend when the config is saved
    };
  }
};

